import { useMemo } from 'react';
import { baseFirmUrl, companySummaryUrl } from 'common/config/urls';
import type { FirmCompany } from 'common/types/company';
import type { Firm } from 'common/types/firm';
import type { UseStoreValue } from 'common/types/store';
import type { CheckFeatureFunction } from 'components/FeatureEnabler/types';
import { COMPANY_NAVIGATION_ROUTES } from 'pages/Company/companyNavigation';
import type { SuperSearchContext, SuperSearchPendingPage } from './SuperSearch.types';
import {
  getLocalSuperSearchEntityResults,
  getLocalSuperSearchMeasurementDateResults,
  getLocalSuperSearchPageResults,
} from './SuperSearch.utils';
import {
  PRIMARY_NAV_INCLUDED_COMPANY_TYPES,
  PRIMARY_NAV_ITEMS,
} from '../PrimaryNavigation/PrimaryNavigation.constants';

interface UseSuperSearchLocalResultsProps {
  checkFeature: CheckFeatureFunction;
  companyList: UseStoreValue['companyList'];
  companyMeasurementDateList: UseStoreValue['companyMeasurementDateList'];
  context: SuperSearchContext;
  currentMeasurementDate: UseStoreValue['currentMeasurementDate'];
  firmList: UseStoreValue['firmList'];
  initialContext: SuperSearchContext;
  locationSearch: string;
  normalizedQuery: string;
  pendingPage?: SuperSearchPendingPage;
  pinnedCompanyIds: ReadonlySet<number>;
  pinnedFirmIds: ReadonlySet<number>;
}

const useSuperSearchLocalResults = ({
  checkFeature,
  companyList,
  companyMeasurementDateList,
  context,
  currentMeasurementDate,
  firmList,
  initialContext,
  locationSearch,
  normalizedQuery,
  pendingPage,
  pinnedCompanyIds,
  pinnedFirmIds,
}: UseSuperSearchLocalResultsProps) => {
  const firmIconUrlsById = useMemo(
    () =>
      new Map(
        ((Array.isArray(firmList) ? firmList : []) as Firm[]).flatMap(firm =>
          typeof firm.id === 'number' && firm.icon_url ? [[firm.id, firm.icon_url] as const] : []
        )
      ),
    [firmList]
  );
  const localSearchEntities = useMemo(() => {
    const activeFirm = context.firm;
    const loadedCompaniesFirm = activeFirm ?? initialContext.firm;
    const firms = ((Array.isArray(firmList) ? firmList : []) as Firm[])
      .filter(firm => !activeFirm || firm.id === activeFirm.id)
      .map(firm => ({
        id: firm.id,
        name: firm.name,
        path: baseFirmUrl(firm.slug),
        slug: firm.slug,
      }))
      .sort((left, right) => {
        const pinnedDifference = Number(pinnedFirmIds.has(right.id)) - Number(pinnedFirmIds.has(left.id));
        if (pinnedDifference !== 0) {
          return pinnedDifference;
        }

        const currentDifference = Number(right.id === activeFirm?.id) - Number(left.id === activeFirm?.id);
        return currentDifference || left.name.localeCompare(right.name);
      });
    const companies =
      loadedCompaniesFirm && loadedCompaniesFirm.id === initialContext.firm?.id
        ? ((Array.isArray(companyList) ? companyList : []) as FirmCompany[])
          .filter(company => !company.company_type || PRIMARY_NAV_INCLUDED_COMPANY_TYPES.has(company.company_type))
          .map(company => ({
            aliases: company.aliases,
            firmId: loadedCompaniesFirm.id,
            id: company.company_id,
            name: company.company_name,
            path: companySummaryUrl(loadedCompaniesFirm.slug, company.company_slug),
            slug: company.company_slug,
          }))
          .sort((left, right) => {
            const pinnedDifference = Number(pinnedCompanyIds.has(right.id)) - Number(pinnedCompanyIds.has(left.id));
            if (pinnedDifference !== 0) {
              return pinnedDifference;
            }

            const leftIsCurrentCompany = left.id === context.company?.id;
            const rightIsCurrentCompany = right.id === context.company?.id;
            const currentDifference = Number(rightIsCurrentCompany) - Number(leftIsCurrentCompany);
            return currentDifference || left.name.localeCompare(right.name);
          })
        : [];

    return { companies, firms };
  }, [companyList, context.company?.id, context.firm, firmList, initialContext.firm, pinnedCompanyIds, pinnedFirmIds]);
  const localEntityResults = useMemo(
    () =>
      getLocalSuperSearchEntityResults({
        ...localSearchEntities,
        query: normalizedQuery,
      }),
    [localSearchEntities, normalizedQuery]
  );
  const quickAccessEntityResults = useMemo(
    () =>
      getLocalSuperSearchEntityResults({
        ...localSearchEntities,
        query: '',
      }),
    [localSearchEntities]
  );
  const localMeasurementDateSearchData = useMemo(() => {
    if (!context.firm) {
      return undefined;
    }

    const companies = (Array.isArray(companyList) ? companyList : []) as FirmCompany[];
    const currentCompany = companies.find(company => company.company_id === context.company?.id);
    const currentCompanyDates = context.company
      ? (companyMeasurementDateList ?? currentCompany?.company_measurement_dates ?? [])
      : companies.flatMap(company => company.company_measurement_dates ?? []);
    const measurementDatesByDate = new Map(
      currentCompanyDates.flatMap(measurementDate => {
        if (typeof measurementDate.id !== 'number' || !measurementDate.slug) {
          return [];
        }

        return [
          [
            measurementDate.date,
            {
              date: measurementDate.date,
              id: measurementDate.id,
              name: 'name' in measurementDate ? measurementDate.name : measurementDate.date,
              slug: measurementDate.slug,
            },
          ] as const,
        ];
      })
    );
    const path = context.company
      ? companySummaryUrl(context.firm.slug, context.company.slug)
      : `${baseFirmUrl(context.firm.slug)}/intelligence/summaries/firm-summary`;

    return {
      context,
      measurementDates: Array.from(measurementDatesByDate.values()),
      path,
    };
  }, [companyList, companyMeasurementDateList, context]);
  const localMeasurementDateResults = useMemo(() => {
    if (!localMeasurementDateSearchData) {
      return [];
    }

    return getLocalSuperSearchMeasurementDateResults({
      ...localMeasurementDateSearchData,
      query: normalizedQuery,
    });
  }, [localMeasurementDateSearchData, normalizedQuery]);
  const quickAccessMeasurementDateResults = useMemo(() => {
    if (!localMeasurementDateSearchData) {
      return [];
    }

    return getLocalSuperSearchMeasurementDateResults({
      ...localMeasurementDateSearchData,
      query: '',
    });
  }, [localMeasurementDateSearchData]);
  const localPageSearchData = useMemo(() => {
    if (!context.firm || pendingPage) {
      return undefined;
    }

    const { firm } = context;
    const firmPages = PRIMARY_NAV_ITEMS.map(page => ({
      key: page.slug,
      label: page.label,
      path: `${baseFirmUrl(firm.slug)}/${page.slug}`,
    }));
    const companyPages = COMPANY_NAVIGATION_ROUTES.filter(
      page => !page.hidden && (!page.featureFlag || checkFeature(page.featureFlag))
    ).map(page => ({
      key: page.slug,
      label: page.title,
      path: context.company ? page.route(firm.slug, context.company.slug) : baseFirmUrl(firm.slug),
      requiredFeatureFlag: page.featureFlag,
    }));

    return {
      companyPages,
      context,
      firmPages,
    };
  }, [checkFeature, context, pendingPage]);
  const localPageResults = useMemo(() => {
    if (!localPageSearchData) {
      return [];
    }

    return getLocalSuperSearchPageResults({
      ...localPageSearchData,
      query: normalizedQuery,
    });
  }, [localPageSearchData, normalizedQuery]);
  const quickAccessPageResults = useMemo(() => {
    if (!localPageSearchData) {
      return [];
    }

    return getLocalSuperSearchPageResults({
      ...localPageSearchData,
      query: '',
    });
  }, [localPageSearchData]);
  const activeMeasurementDate = useMemo(
    () =>
      new URLSearchParams(locationSearch).get('date') ??
      currentMeasurementDate?.date ??
      companyMeasurementDateList?.[0]?.date,
    [companyMeasurementDateList, currentMeasurementDate?.date, locationSearch]
  );

  return {
    activeMeasurementDate,
    firmIconUrlsById,
    localEntityResults,
    localMeasurementDateResults,
    localPageResults,
    quickAccessEntityResults,
    quickAccessMeasurementDateResults,
    quickAccessPageResults,
  };
};

export default useSuperSearchLocalResults;
